"use client";

import { cn } from "@/lib/utils";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useEffect, useRef, useState } from "react";
import ex1Medium from "@/assets/videos/ex1_medium.mp4";
import ex1Large from "@/assets/videos/ex1_large.mp4";
import ex2Medium from "@/assets/videos/ex2_medium.mp4";
import ex2Large from "@/assets/videos/ex2_large.mp4";
import ex3Medium from "@/assets/videos/ex3_medium.mp4";
import ex3Large from "@/assets/videos/ex3_large.mp4";
import { useTranslation } from "react-i18next";
import { VideoWithShimmer } from "@/components/ui/video-with-shimmer";

function PlayIcon() {
  return (
    <svg
      className={cn(
        "size-10 ml-1",
        "2k:size-12 2k:ml-2",
        "4k:size-14 4k:ml-3"
      )}
      viewBox="0 0 30 34"
      fill="none"
    >
      <path d="M28.5 14.9933C29.8333 15.7631 29.8333 17.6876 28.5 18.4574L3 33.1798C1.66666 33.9496 -1.62878e-06 32.9874 -1.56148e-06 31.4478L-2.74401e-07 2.00291C-2.07103e-07 0.463308 1.66667 -0.498942 3 0.270858L28.5 14.9933Z" fill="white" />
    </svg>
  );
}

interface VideoExamplesSectionProps extends React.HTMLAttributes<HTMLElement> {}

function VideoExamplesSection({ className, ...props }: VideoExamplesSectionProps) {
  const titleRef = useRef<HTMLDivElement>(null);
  const videosRef = useRef<HTMLDivElement>(null);
  const videoRef1 = useRef<HTMLVideoElement>(null);
  const videoRef2 = useRef<HTMLVideoElement>(null);
  const videoRef3 = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState<Record<number, boolean>>({
    1: true,
    2: true,
    3: true,
  });
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  
  useEffect(() => {
    const check = () => setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const togglePlay = (index: number, ref: React.RefObject<HTMLVideoElement | null>) => {
    const video = ref.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      setPlaying((prev) => ({ ...prev, [index]: true }));
    } else {
      video.pause();
      setPlaying((prev) => ({ ...prev, [index]: false }));
    }
  };

  useGSAP(() => {
    const ctx = gsap.context(async () => {
      if (titleRef.current) {
        gsap.set(titleRef.current, { opacity: 0 });
        gsap.fromTo(
          titleRef.current,
          { opacity: 0 },
          {
            opacity: 1,
            scrollTrigger: {
              trigger: titleRef.current,
              start: "top center",
              end: "top 30%",
              scrub: 1,
            },
          }
        );
      }

      if (videosRef.current) {
        const videoChildren = Array.from(videosRef.current.children);
        videoChildren.forEach((child) => {
          gsap.set(child, { opacity: 0, y: 60 });
        });
        gsap.fromTo(
          videoChildren,
          { opacity: 0, y: 60 },
          {
            opacity: 1,
            y: 0,
            stagger: 0.15,
            scrollTrigger: {
              trigger: titleRef.current,
              start: "top center",
              end: "center center",
              scrub: 1,
            },
          }
        );
      }
    });

    return () => ctx.revert();
  });

  return (
    <section
      id="video-examples"
      className={cn("px-6 pt-18 pb-10", "md:pb-28", "4k:pt-24 4k:pb-32", className)}
      {...props}
    >
      <div
        ref={titleRef}
        className={cn(
          "flex flex-col gap-4 mb-6",
          "md:flex-row md:items-center md:mb-12 md:gap-12",
          "4k:mb-30 4k:gap-18"
        )}
      >
        <p className={`${isMobile ? 'text-[42px] font-extrabold' : 'font-h1'}`}>{t("videoExamples.title")}</p>
        <p className="font-paragraph">
          {isMobile ? 
            t("videoExamples.subtitle").replaceAll("\\n", "") : 
            t("videoExamples.subtitle").split('\n').map((line, i) => (
              <span key={i}>{line}{i === 0 && <br />}</span>
            ))
          }
        </p>
      </div>

      <div
        ref={videosRef}
        className={cn(
          "grid grid-cols-1 gap-4",
          "md:grid-cols-[1fr_auto_1fr] md:gap-6",
          "4k:gap-12"
        )}
      >
        {/* Video 1 - landscape */}
        <div
          className={cn(
            "relative w-full overflow-hidden rounded-[5px] cursor-pointer",
            "2k:rounded-lg",
            "4k:rounded-xl"
          )}
          onClick={() => togglePlay(1, videoRef1)}
        >
          <VideoWithShimmer
            autoPlay
            ref={videoRef1}
            muted
            preload="metadata"
            playsInline
            loop
            containerClassName="h-[200px] md:h-[335px] 2k:h-[500px] 4k:h-[700px] w-full"
            className={cn("w-full h-full object-cover")}
            onEnded={() => setPlaying((prev) => ({ ...prev, 1: false }))}
          >
            <source src={ex1Large} type="video/mp4" media="(min-width: 1920px)" />
            <source src={ex1Medium} type="video/mp4" />
          </VideoWithShimmer>
          {!playing[1] && (
            <div className="absolute inset-0 flex items-center justify-center">
              <PlayIcon />
            </div>
          )}
        </div>

        {/* Video 2 - portrait (center) */}
        <div
          className={cn(
            "relative w-full overflow-hidden rounded-[5px] cursor-pointer",
            "md:w-auto",
            "2k:rounded-lg",
            "4k:rounded-xl"
          )}
          onClick={() => togglePlay(2, videoRef2)}
        >
          <VideoWithShimmer
            autoPlay
            ref={videoRef2}
            muted
            preload="metadata"
            playsInline
            loop
            containerClassName="h-[505px] md:h-[335px] 2k:h-[500px] 4k:h-[700px] w-full md:w-auto md:aspect-3/4"
            className={cn("w-full h-full object-cover")}
            onEnded={() => setPlaying((prev) => ({ ...prev, 2: false }))}
          >
            <source src={ex2Large} type="video/mp4" />
          </VideoWithShimmer>
          {!playing[2] && (
            <div className="absolute inset-0 flex items-center justify-center">
              <PlayIcon />
            </div>
          )}
        </div>

        {/* Video 3 - landscape */}
        <div
          className={cn(
            "relative w-full overflow-hidden rounded-[5px] cursor-pointer",
            "2k:rounded-lg",
            "4k:rounded-xl"
          )}
          onClick={() => togglePlay(3, videoRef3)}
        >
          <VideoWithShimmer
            autoPlay
            ref={videoRef3}
            muted
            preload="metadata"
            playsInline
            loop
            containerClassName="h-[200px] md:h-[335px] 2k:h-[500px] 4k:h-[700px] w-full"
            className={cn("w-full h-full object-cover object-[100%_center]")}
            onEnded={() => setPlaying((prev) => ({ ...prev, 3: false }))}
          >
            <source src={ex3Large} type="video/mp4" media="(min-width: 1920px)" />
            <source src={ex3Medium} type="video/mp4" />
          </VideoWithShimmer>
          {!playing[3] && (
            <div className="absolute inset-0 flex items-center justify-center">
              <PlayIcon />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export { VideoExamplesSection };
